import { type BaseQueryApi, createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { healthFromRestV1 } from 'domain/mappers/healthFromRestV1';
import { versionFromRestV1 } from 'domain/mappers/versionFromRestV1';
import type { HealthStatusFE, SystemVersionFE } from 'domain/system';
import type { RootState } from 'store/reducers';
import { getSessionExpiredDialogOpen, hideSessionExpiredDialog, showSessionExpiredDialog } from 'store/slices/appSlice';
import { validateSession } from 'store/tokenHandling/validateSession';

import { errorHandler, formatAxiosError, isAxiosError } from './errors';
import { correlationHeaders, monitoringApi } from './restClient';

const getHealthStatusFn = async (
    _arg: string,
    api: Pick<BaseQueryApi, 'dispatch' | 'getState'>
): Promise<{ data: HealthStatusFE }> => {
    try {
        const { status } = await monitoringApi.get<unknown>('/api/v1/health/status', {
            ...correlationHeaders(),
            validateStatus: () => true,
            transformResponse: [],
        });
        // `validateSession` has already signed out by the time it reports `false`, so this only
        if (await validateSession()) {
            if (getSessionExpiredDialogOpen(api.getState() as RootState)) {
                api.dispatch(hideSessionExpiredDialog());
            }
        } else if (!getSessionExpiredDialogOpen(api.getState() as RootState)) {
            api.dispatch(showSessionExpiredDialog());
        }

        return { data: healthFromRestV1(status) };
    } catch {
        // Silent, like the backend resolver: at a 5s poll a log line per failure would flood
        // Datadog for the whole outage, and `SystemHealthNotification` is the user-facing signal.
        return { data: healthFromRestV1(undefined) };
    }
};

// `/api/v1/system/version`, not `/api/v1/version` — the latter answers 404 `No path configured`
// from the gateway on both staging and preprod.
const getSystemVersionFn = async (): Promise<{ data: SystemVersionFE }> => {
    try {
        const response = await monitoringApi.get<unknown>('/api/v1/system/version', correlationHeaders());
        return { data: versionFromRestV1(response.data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler(
            '[getSystemVersion]: Unexpected error occurred while getting the system api version',
            loggableError
        );
    }
};

export const systemApi = createApi({
    reducerPath: 'system',
    // Every endpoint runs on `queryFn`, so this is never invoked — and keeping a GraphQL base
    // query on a slice that issues no GraphQL request would be misleading.
    baseQuery: fakeBaseQuery<unknown>(),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        // The `string` arg is the cache key, not an input — `'GetHealthStatus'`, unchanged from
        // the GraphQL endpoint. Do not "clean up" the unread argument.
        getHealthStatus: builder.query<HealthStatusFE, string>({
            queryFn: getHealthStatusFn,
        }),
        getSystemVersion: builder.query<SystemVersionFE, string>({
            queryFn: getSystemVersionFn,
        }),
    }),
});

export const { useGetHealthStatusQuery, useLazyGetSystemVersionQuery } = systemApi;
